import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  inject,
  input,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ShowDetails } from 'www-cms';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import { EventService, ShowPriceInfoPipe } from 'api';
import { map, shareReplay, switchMap } from 'rxjs';
import { getDateYear, LocationInfoPipe } from 'common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DirectusDirective } from 'directus';

@Component({
  selector: 'ss-www-show-details',
  imports: [
    CommonModule,
    LocationInfoPipe,
    ShowPriceInfoPipe,
    DirectusDirective,
  ],
  templateUrl: './show-details.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowDetailsComponent extends BaseBuildingBlockComponent {
  private eventService = inject(EventService);

  readonly showDetailsData = input.required<ShowDetails>();
  readonly showDetailsId = computed(() => this.showDetailsData().id);

  private datePipe = new DatePipe('en-US');

  event$ = toObservable(this.showDetailsData).pipe(
    switchMap((showDetails) => {
      if (!showDetails.show.showCode) {
        throw new Error('Show code is required');
      }
      return this.eventService.getEvent(showDetails.show.showCode);
    }),
    shareReplay()
  );

  event = toSignal(this.event$);
  showYear: Signal<number> = toSignal(
    this.event$.pipe(
      map((event) => {
        const startDate = event.eventDetails?.eventStartDate;
        if (!startDate) {
          console.error('No start date found');
          return 0;
        }
        return getDateYear(new Date(startDate));
      })
    ),
    { initialValue: 0 }
  );

  runTime$ = this.event$.pipe(
    map((event) => {
      if (event.eventDetails?.eventTimeMinutes) {
        return `${Math.floor(
          event.eventDetails.eventTimeMinutes / 60
        )} hours and ${event.eventDetails.eventTimeMinutes % 60} minutes`;
      }
      return '';
    })
  );

  startDate$ = this.event$.pipe(
    map((event) => {
      if (!event.eventDetails?.eventStartDate) return '';
      return (
        this.datePipe.transform(event.eventDetails.eventStartDate, 'MMMM  ') +
        this.getOrdinal(new Date(event.eventDetails.eventStartDate).getDate()) +
        // Show year for start only if start and end dates are different years
        (new Date(event.eventDetails.eventStartDate).getFullYear() !==
        new Date(event.eventDetails.eventEndDate).getFullYear()
          ? `, ${new Date(event.eventDetails.eventStartDate).getFullYear()}`
          : '')
      );
    })
  );

  endDate$ = this.event$.pipe(
    map((event) => {
      if (!event.eventDetails?.eventEndDate) return '';
      return (
        this.datePipe.transform(event.eventDetails.eventEndDate, 'MMMM  ') +
        this.getOrdinal(new Date(event.eventDetails.eventEndDate).getDate())
      );
    })
  );

  adultPriceRange$ = this.event$.pipe(
    map((event) => {
      return event.eventPriceListDetail;
    })
  );

  private getOrdinal(n: number) {
    const s = ['th', 'st', 'nd', 'rd'],
      v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
  override getBgClass(): LastItemBgClass {
    return 'bg-black';
  }
}
